'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Plus, CheckCircle2, Clock, AlertCircle, Eye, FileText, ArrowLeft, Bird, ShoppingCart, Skull, XCircle, Save, X } from 'lucide-react';
import { Card, KPICard } from '@/components/ui/Cards';
import { MOCK_DAILY_RECORDS, MOCK_FLOCK_SUMMARIES, FARMS, HOUSES_BY_FARM, type DailyBirdRecord } from '../../components/birdRecordsData';
import Icon from '@/components/ui/AppIcon';


// ─── Status Badge ─────────────────────────────────────────────────────────────

function RecordStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string; icon: React.ElementType }> = {
    draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-600', icon: Clock },
    submitted: { label: 'Submitted', cls: 'bg-amber-100 text-amber-700', icon: Clock },
    approved: { label: 'Approved', cls: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  };
  const s = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600', icon: Clock };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      <Icon size={10} />
      {s.label}
    </span>
  );
}

// ─── New Daily Record Form ────────────────────────────────────────────────────

function NewDailyRecordForm({ onClose }: { onClose: () => void }) {
  const [farm, setFarm] = useState('Idudi Poultry Farm');
  const [flock, setFlock] = useState('');
  const [mortality, setMortality] = useState(0);
  const [culling, setCulling] = useState(0);
  const [transfersIn, setTransfersIn] = useState(0);
  const [transfersOut, setTransfersOut] = useState(0);
  const [sold, setSold] = useState(0);

  const selectedFlock = MOCK_FLOCK_SUMMARIES.find(f => f.flockId === flock);
  const opening = selectedFlock?.currentBirds ?? 0;
  const closing = opening + transfersIn - transfersOut - mortality - culling - sold;

  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">New Daily Bird Record</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Record today's bird population changes</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
          <X size={14} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Date */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Date *</label>
          <input
            type="date"
            defaultValue="2026-09-06"
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>

        {/* Farm */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Farm *</label>
          <select
            value={farm}
            onChange={(e) => setFarm(e.target.value)}
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            {FARMS.map(f => <option key={f}>{f}</option>)}
          </select>
        </div>

        {/* House */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Poultry House *</label>
          <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
            <option value="">Select house...</option>
            {(HOUSES_BY_FARM[farm] ?? []).map(h => <option key={h}>{h}</option>)}
          </select>
        </div>

        {/* Flock */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Flock *</label>
          <select
            value={flock}
            onChange={(e) => setFlock(e.target.value)}
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            <option value="">Select flock...</option>
            {MOCK_FLOCK_SUMMARIES.filter(f => f.farm === farm).map(f => (
              <option key={f.flockId} value={f.flockId}>{f.flockNumber} — {f.flockName}</option>
            ))}
          </select>
        </div>

        {/* Opening Population (auto) */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Opening Population</label>
          <div className="w-full text-xs border border-border/40 rounded-lg px-3 py-2 bg-muted/30 text-foreground font-tabular font-semibold">
            {opening.toLocaleString()}
            <span className="text-muted-foreground font-normal ml-1">(auto from previous closing)</span>
          </div>
        </div>

        {/* Mortality */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Mortality</label>
          <input
            type="number"
            min={0}
            value={mortality}
            onChange={(e) => setMortality(Number(e.target.value))}
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>

        {/* Culling */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Culled</label>
          <input
            type="number"
            min={0}
            value={culling}
            onChange={(e) => setCulling(Number(e.target.value))}
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>

        {/* Transfers In */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Transfers In</label>
          <input
            type="number"
            min={0}
            value={transfersIn}
            onChange={(e) => setTransfersIn(Number(e.target.value))}
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>

        {/* Transfers Out */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Transfers Out</label>
          <input
            type="number"
            min={0}
            value={transfersOut}
            onChange={(e) => setTransfersOut(Number(e.target.value))}
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>

        {/* Birds Sold */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Birds Sold</label>
          <input
            type="number"
            min={0}
            value={sold}
            onChange={(e) => setSold(Number(e.target.value))}
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>

        {/* Notes */}
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="text-xs font-medium text-muted-foreground block mb-1">Notes</label>
          <textarea
            rows={2}
            placeholder="Any observations or notes for today..."
            className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none"
          />
        </div>
      </div>

      {/* Closing Population Preview */}
      {flock && (
        <div className="mt-4 p-3 rounded-xl border border-border/60 bg-muted/20">
          <p className="text-xs font-semibold text-foreground mb-2">Population Calculation Preview</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-muted-foreground">Opening</p>
              <p className="font-tabular font-semibold text-foreground">{opening.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Transfers In</p>
              <p className="font-tabular font-semibold text-green-600">+{transfersIn.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Mortality + Culled + Sold + Out</p>
              <p className="font-tabular font-semibold text-red-600">-{(mortality + culling + sold + transfersOut).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground font-semibold">Closing Population</p>
              <p className={`font-tabular font-bold text-base ${closing < 0 ? 'text-red-600' : 'text-green-700'}`}>
                {closing < 0 ? '⚠ Negative!' : closing.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border/60">
        <button className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
          <Save size={12} />
          Submit Record
        </button>
        <button className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors">
          Save Draft
        </button>
        <button onClick={onClose} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
          Cancel
        </button>
      </div>
    </Card>
  );
}

// ─── Main Content ─────────────────────────────────────────────────────────────

export default function DailyBirdRecordsContent() {
  const [showForm, setShowForm] = useState(false);
  const [filterDate, setFilterDate] = useState('2026-09-06');

  const todayRecords = MOCK_DAILY_RECORDS.filter(r => r.date === filterDate);
  const totalOpening = todayRecords.reduce((s, r) => s + r.openingPopulation, 0);
  const totalMortality = todayRecords.reduce((s, r) => s + r.mortality, 0);
  const totalCulling = todayRecords.reduce((s, r) => s + r.culling, 0);
  const totalSold = todayRecords.reduce((s, r) => s + r.birdsSold, 0);
  const totalClosing = todayRecords.reduce((s, r) => s + r.closingPopulation, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link href="/bird-records" className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
            <ArrowLeft size={14} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Daily Bird Records</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Daily population tracking across all flocks and houses</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="text-xs border border-border/60 rounded-lg px-3 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus size={12} />
            New Record
          </button>
        </div>
      </div>

      {/* New Form */}
      {showForm && <NewDailyRecordForm onClose={() => setShowForm(false)} />}

      {/* Daily Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <KPICard label="Opening Population" value={totalOpening.toLocaleString()} icon={Bird} iconBg="#eff6ff" iconColor="#2563eb" />
        <KPICard label="Mortality" value={totalMortality.toLocaleString()} icon={Skull} iconBg="#fef2f2" iconColor="#dc2626" variant="danger" />
        <KPICard label="Culled" value={totalCulling.toLocaleString()} icon={XCircle} iconBg="#fef2f2" iconColor="#b91c1c" />
        <KPICard label="Birds Sold" value={totalSold.toLocaleString()} icon={ShoppingCart} iconBg="#faf5ff" iconColor="#9333ea" />
        <KPICard label="Closing Population" value={totalClosing.toLocaleString()} icon={CheckCircle2} iconBg="#f0fdf4" iconColor="#15803d" variant="success" />
      </div>

      {/* Daily Report Table */}
      <Card padding="sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Daily Bird Population Report</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Y77 Poultry — {new Date(filterDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors">
            <FileText size={12} />
            Export
          </button>
        </div>

        {todayRecords.length === 0 ? (
          <div className="text-center py-8">
            <Bird size={32} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No daily records for this date</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-3 text-xs text-primary hover:underline"
            >
              Create the first record
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/60">
                  {['Flock', 'Farm / House', 'Opening', 'In', 'Transfers In', 'Transfers Out', 'Mortality', 'Cull', 'Sold', 'Closing', 'Recorded By', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="text-left py-2 px-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {todayRecords.map((r) => (
                  <tr key={r.id} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-2 font-medium text-primary whitespace-nowrap">{r.flockNumber}</td>
                    <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                      <div className="text-foreground">{r.farmName}</div>
                      <div className="text-muted-foreground/70">{r.houseName}</div>
                    </td>
                    <td className="py-2 px-2 font-tabular text-foreground">{r.openingPopulation.toLocaleString()}</td>
                    <td className="py-2 px-2 font-tabular text-green-600">+{r.birdsAdded.toLocaleString()}</td>
                    <td className="py-2 px-2 font-tabular text-blue-600">+{r.transfersIn.toLocaleString()}</td>
                    <td className="py-2 px-2 font-tabular text-amber-600">-{r.transfersOut.toLocaleString()}</td>
                    <td className="py-2 px-2 font-tabular text-red-600">-{r.mortality.toLocaleString()}</td>
                    <td className="py-2 px-2 font-tabular text-orange-600">-{r.culling.toLocaleString()}</td>
                    <td className="py-2 px-2 font-tabular text-purple-600">-{r.birdsSold.toLocaleString()}</td>
                    <td className="py-2 px-2 font-tabular font-semibold text-foreground">{r.closingPopulation.toLocaleString()}</td>
                    <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">{r.recordedBy}</td>
                    <td className="py-2 px-2"><RecordStatusBadge status={r.status} /></td>
                    <td className="py-2 px-2">
                      <button className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors">
                        <Eye size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="border-t-2 border-border/60 bg-muted/20 font-semibold">
                  <td className="py-2 px-2 text-foreground" colSpan={2}>TOTAL</td>
                  <td className="py-2 px-2 font-tabular text-foreground">{totalOpening.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-green-600">+0</td>
                  <td className="py-2 px-2 font-tabular text-blue-600">+{todayRecords.reduce((s, r) => s + r.transfersIn, 0).toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-amber-600">-{todayRecords.reduce((s, r) => s + r.transfersOut, 0).toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-red-600">-{totalMortality.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-orange-600">-{totalCulling.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-purple-600">-{totalSold.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular font-bold text-foreground">{totalClosing.toLocaleString()}</td>
                  <td colSpan={3} />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Missing Records Alert */}
      <Card padding="sm" alert alertVariant="warning">
        <div className="flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-amber-700">Missing Daily Records</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              The following flocks have no daily record for {new Date(filterDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}:
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {MOCK_FLOCK_SUMMARIES
                .filter(f => f.status === 'active' && !todayRecords.find(r => r.flockId === f.flockId))
                .map(f => (
                  <span key={f.flockId} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 font-medium">
                    {f.flockNumber}
                  </span>
                ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
